export const PDF_FILENAME_FORMATS = ["idFirst", "nameFirst", "nameOnly", "idOnly"] as const;

export type PdfFilenameFormat = (typeof PDF_FILENAME_FORMATS)[number];

export const DEFAULT_PDF_FILENAME_FORMAT: PdfFilenameFormat = "idFirst";

export function isPdfFilenameFormat(value: unknown): value is PdfFilenameFormat {
  return PDF_FILENAME_FORMATS.includes(value as PdfFilenameFormat);
}

interface PrintablePatientLike {
  name?: string | null;
  uuid?: string | null;
}

interface PrintableResultLike {
  eye?: string | null;
  patient?: PrintablePatientLike | null;
  category?: string | null;
  testType?: string | null;
  startDate?: string | Date | null;
}

function sanitizeFilenameSegment(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const sanitized = value
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "")
    .replace(/\s+/g, "-")
    .replace(/\.+$/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

  return sanitized.length > 0 ? sanitized : null;
}

function getEyeFilenameLabel(eye?: string | null): string | null {
  switch (eye) {
    case "rightEye":
      return "OD";
    case "leftEye":
      return "OS";
    case "bothEye":
    case "rightThenLeft":
      return "OU";
    default:
      return null;
  }
}

function getTestFilenameLabel(result?: PrintableResultLike | null): string {
  const categoryLabel = sanitizeFilenameSegment(result?.category);
  if (categoryLabel) {
    return categoryLabel.toLowerCase();
  }

  return sanitizeFilenameSegment(result?.testType)?.toLowerCase() ?? "report";
}

function formatFilenameDate(dateValue?: string | Date | null): string | null {
  if (!dateValue) {
    return null;
  }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function buildTestResultFilename(
  result?: PrintableResultLike | null,
  format: PdfFilenameFormat = DEFAULT_PDF_FILENAME_FORMAT,
): string {
  const eye = getEyeFilenameLabel(result?.eye);
  const name = sanitizeFilenameSegment(result?.patient?.name);
  const id = sanitizeFilenameSegment(result?.patient?.uuid);
  const test = getTestFilenameLabel(result);
  const date = formatFilenameDate(result?.startDate);

  const layouts: Record<PdfFilenameFormat, (string | null)[]> = {
    idFirst: [eye, id, test, date, name],
    nameFirst: [name, id, eye, test, date],
    nameOnly: [name, eye, test, date],
    idOnly: [id, eye, test, date],
  };

  const parts = layouts[format].filter(Boolean);

  return `${parts.join("-") || "report"}.pdf`;
}

export function buildScreeningReportFilename(
  patientName?: string | null,
  startDate?: string | Date | null,
): string {
  const parts = [
    "oculera-report",
    sanitizeFilenameSegment(patientName?.toLowerCase()),
    formatFilenameDate(startDate),
  ].filter(Boolean);
  return `${parts.join("-")}.pdf`;
}

export function replaceFilenameExtension(filename: string, extension: string): string {
  const normalizedExtension = extension.startsWith(".") ? extension : `.${extension}`;
  return filename.replace(/\.[^.]+$/, normalizedExtension);
}
